import React, {useEffect, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {IStore} from 'redux/interface';
import {RoutesUrl, TranslationKeys} from 'utils/constants/routeConstants';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {
  getAssistantSession,
  getPatientAssignments,
  getPatientMedications,
  getProgramModules,
  interventionAsyncActions
} from 'redux/pageAsyncActions';
import {
  IPatientAssignment,
  IProgramModule,
  InterventionStatusCode
} from 'redux/intervention/interfaces';

import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';
import {ProgramProgressText} from 'components/program-progress';
import {ITodayActivityItem, TodayActivity, isDueToday} from 'components/today-activity';

import NotFoundImg from 'assets/image/common/notFound.png';

import {getCurrentModuleItem} from 'utils/helpers/getCurrentModuleItem';
import {useInterventionSse} from 'utils/hooks/useInterventionSse';
import {useMedicationCheckins} from 'utils/hooks/useMedicationCheckins';
import {countDoses, dosesForDay} from 'pages/medications/medicationDoses';

import './styles.scss';

const UP_NEXT_STATUSES = new Set<InterventionStatusCode>(['enrolled', 'in_progress', 'screening', 'paused']);

const STATUS_LABELS: Record<InterventionStatusCode, string> = {
  enrolled: 'Enrolled',
  in_progress: 'In progress',
  screening: 'Screening',
  paused: 'Paused',
  requested: 'Requested',
  completed: 'Completed',
  withdrawn: 'Withdrawn',
  screen_failed: 'Screen Failed'
};

// ── Greeting (4.1) ───────────────────────────────────────────────────────────

const Greeting: React.FC = () => {
  const {userProfile} = useSelector((state: IStore) => state.auth);

  return (
    <h1 className="patient-dashboard__greeting">
      <FormattedMessage
        id="gritx.dashboard.greeting"
        defaultMessage="Hi{name}"
        values={{name: userProfile?.firstName ? `, ${userProfile.firstName}` : ''}}
      />
    </h1>
  );
};

// ── Latest notification ──────────────────────────────────────────────────────

// Placeholder only: the dashboard will show the most recent in-app notification here once that
// task lands, replacing the per-status banners this slot used to hold.
const LatestNotification: React.FC = () => (
  <section className="patient-dashboard-notification">
    <div className="patient-dashboard-notification__label">
      <FormattedMessage id="gritx.dashboard.latestNotification" defaultMessage="Latest notification"/>
    </div>
    <div className="patient-dashboard-notification__text">
      <FormattedMessage id="gritx.dashboard.noNotifications" defaultMessage="No new notifications."/>
    </div>
  </section>
);

// ── Today's medications ──────────────────────────────────────────────────────

const PillIcon: React.FC = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <g transform="rotate(-45 12 12)">
      <rect x="2" y="8" width="20" height="8" rx="4" fill="#fff" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 8h6a4 4 0 0 1 0 8h-6z" fill="currentColor" opacity="0.35"/>
      <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="1.5"/>
    </g>
  </svg>
);

// Summary of today's doses, from the same hook as the Medications page so the two cannot disagree.
// Hidden while loading, when the request fails, and when no dose is scheduled today: medications
// must never break or clutter the dashboard.
const MedicationsBanner: React.FC = () => {
  const navigate = useNavigate();
  const {doses, loadState} = useMedicationCheckins();
  const counts = useMemo(() => countDoses(dosesForDay(doses, new Date())), [doses]);

  if (loadState !== 'ready' || counts.total === 0) {
    return null;
  }

  const pct = Math.round((counts.taken / counts.total) * 100);

  return (
    <div className="patient-dashboard__medications">
      <button type="button" className="patient-dashboard-medications" onClick={() => navigate(RoutesUrl.Medications)}>
        <span className="patient-dashboard-medications__icon"><PillIcon/></span>
        <span className="patient-dashboard-medications__body">
          <span className="patient-dashboard-medications__title">
            <FormattedMessage id="gritx.dashboard.medications.title" defaultMessage="Today's medications"/>
          </span>
          <span className="patient-dashboard-medications__subtitle">
            <FormattedMessage
              id="gritx.dashboard.medications.summary"
              defaultMessage="{taken} taken · {pending} pending"
              values={{taken: counts.taken, pending: counts.pending}}
            />
            {counts.skipped > 0 && (
              <>
                {' · '}
                <FormattedMessage
                  id="gritx.dashboard.medications.skipped"
                  defaultMessage="{count} skipped"
                  values={{count: counts.skipped}}
                />
              </>
            )}
            {counts.missed > 0 && (
              <>
                {' · '}
                <FormattedMessage
                  id="gritx.dashboard.medications.missed"
                  defaultMessage="{count} missed"
                  values={{count: counts.missed}}
                />
              </>
            )}
          </span>
          <span className="patient-dashboard-medications__bar">
            <span className="patient-dashboard-medications__bar-fill" style={{width: `${pct}%`}}/>
          </span>
        </span>
        <span className="patient-dashboard-medications__arrow" aria-hidden="true">→</span>
      </button>
    </div>
  );
};

// ── Today's activity ─────────────────────────────────────────────────────────

// One current step per active module (see getCurrentModuleItem), per program, when it is due
// today or earlier. Built from the modules list because the pending-run endpoint filters on
// step statuses the engine no longer writes, so it never lists new steps such as screening.
function buildTodayItemsByProgram(
  assignments: IPatientAssignment[],
  programModules: Record<number, IProgramModule[]>,
  navigate: (path: string) => void
): Record<number, ITodayActivityItem[]> {
  const byProgram: Record<number, ITodayActivityItem[]> = {};

  assignments.forEach((a) => {
    const apId = a.activityParticipantId;
    const items: ITodayActivityItem[] = [];

    (programModules[apId] ?? []).forEach((module) => {
      const item = getCurrentModuleItem(module);

      if (!item || !isDueToday(item.availableAt)) {
        return;
      }

      const query = item.stepKind === 'sip' ? '?stepKind=sip' : '';

      items.push({
        key: `${apId}-${module.moduleId}-${item.itemId}-${item.stepKind}`,
        title: item.title,
        context: module.title === item.title
          ? a.interventionDisplayName
          : `${a.interventionDisplayName} · ${module.title}`,
        time: item.availableAt,
        onClick: () => navigate(`/interventions/${apId}/steps/${item.itemId}${query}`)
      });
    });

    byProgram[apId] = items;
  });

  return byProgram;
}

const TodaysActivity: React.FC<{
  activeAssignments: IPatientAssignment[]
  programModules: Record<number, IProgramModule[]>
  itemsByProgram: Record<number, ITodayActivityItem[]>
}> = ({activeAssignments, programModules, itemsByProgram}) => {
  const isLoaded = activeAssignments.every((a) => Array.isArray(programModules[a.activityParticipantId]));

  if (activeAssignments.length === 0 || !isLoaded) {
    return null;
  }

  const items = activeAssignments.flatMap((a) => itemsByProgram[a.activityParticipantId] ?? []);

  return (
    <section className="patient-dashboard__section">
      <h2 className="patient-dashboard__section-title">
        <FormattedMessage id="gritx.dashboard.todaysActivity" defaultMessage="Today's activity"/>
      </h2>
      <div className="patient-dashboard__activity-list">
        <TodayActivity items={items}/>
      </div>
    </section>
  );
};

// ── My interventions, condensed (4.5) ───────────────────────────────────────

const MyInterventions: React.FC<{
  assignments: IPatientAssignment[]
  pendingCounts: Record<number, number>
}> = ({assignments, pendingCounts}) => {
  const navigate = useNavigate();

  return (
    <section className="patient-dashboard__section">
      <h2 className="patient-dashboard__section-title">
        <FormattedMessage
          id="gritx.dashboard.yourPrograms"
          defaultMessage="Your programs ({count})"
          values={{count: assignments.length}}
        />
      </h2>
      {assignments.length === 0 && (
        <p className="patient-dashboard__empty">
          <FormattedMessage id="gritx.dashboard.noActivePrograms" defaultMessage="You have no active programs."/>
        </p>
      )}
      <div className="patient-dashboard__cards">
        {assignments.map((a) => {
          const pct = a.modulesTotal > 0 ? Math.round((a.modulesCompleted / a.modulesTotal) * 100) : 0;
          const pendingCount = pendingCounts[a.activityParticipantId] ?? 0;

          return (
            <div
              key={a.activityParticipantId}
              className="patient-dashboard-card"
              style={{cursor: 'pointer'}}
              onClick={() => navigate(RoutesUrl.ProgramDetail.replace(':apId', String(a.activityParticipantId)))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate(RoutesUrl.ProgramDetail.replace(':apId', String(a.activityParticipantId)));
                }
              }}
            >
              <div className="patient-dashboard-card__header">
                <span className="patient-dashboard-card__name">{a.interventionDisplayName}</span>
                <span className={`patient-dashboard-card__status patient-dashboard-card__status--${a.statusCode}`}>
                  {STATUS_LABELS[a.statusCode]}
                </span>
              </div>
              <ProgramProgressText
                className="patient-dashboard-card__subtitle"
                modulesCompleted={a.modulesCompleted}
                modulesTotal={a.modulesTotal}
                recurrence={a.recurrence}
                daysElapsed={a.daysElapsed}
                pendingCount={pendingCount}
              />
              {a.modulesTotal > 0 && (
                <div className="patient-dashboard-progress-bar">
                  <div className="patient-dashboard-progress-fill" style={{width: `${pct}%`}}/>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" className="patient-dashboard__see-all" onClick={() => navigate(RoutesUrl.Interventions)}>
        <FormattedMessage id="gritx.dashboard.seeAllPrograms" defaultMessage="See all programs"/>
      </button>
    </section>
  );
};

// ── Quick-link tiles (4.6) ───────────────────────────────────────────────────

const QuickLinkTiles: React.FC<{medicationsCount: number}> = ({medicationsCount}) => {
  const navigate = useNavigate();

  return (
    <section className="patient-dashboard__section patient-dashboard__tiles">
      <button type="button" className="patient-dashboard-tile" onClick={() => navigate(RoutesUrl.Medications)}>
        <span className="patient-dashboard-tile__title">
          <FormattedMessage id="gritx.dashboard.tile.meds" defaultMessage="Meds"/>
        </span>
        <span className="patient-dashboard-tile__count">{medicationsCount}</span>
      </button>
    </section>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const Dashboard: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatchedModules = useRef(new Set<number>());

  const {loading} = useSelector((state: IStore) => state.status);
  const {assignments, programModules, medications} = useSelector((state: IStore) => state.intervention);

  useGetTranslations({translationKey: TranslationKeys.Dashboard});

  useEffect(() => {
    dispatch(interventionAsyncActions[getPatientAssignments]());
    dispatch(interventionAsyncActions[getPatientMedications]());
    dispatch(interventionAsyncActions[getAssistantSession]());
  }, [dispatch]);

  useInterventionSse(assignments);

  const activeAssignments = useMemo(
    () => assignments.filter((a) => UP_NEXT_STATUSES.has(a.statusCode)),
    [assignments]
  );

  useEffect(() => {
    activeAssignments.forEach((a) => {
      if (!dispatchedModules.current.has(a.activityParticipantId)) {
        dispatchedModules.current.add(a.activityParticipantId);
        dispatch(interventionAsyncActions[getProgramModules](a.activityParticipantId));
      }
    });
  }, [activeAssignments, dispatch]);

  const itemsByProgram = useMemo(
    () => buildTodayItemsByProgram(activeAssignments, programModules, navigate),
    [activeAssignments, programModules, navigate]
  );
  const pendingCounts = useMemo(
    () => Object.fromEntries(Object.entries(itemsByProgram).map(([apId, items]) => [apId, items.length])),
    [itemsByProgram]
  );

  const isAssignmentsLoading = loading.includes(getPatientAssignments);

  return (
    <div className="patient-dashboard">
      <Greeting/>

      <LatestNotification/>

      <MedicationsBanner/>

      {isAssignmentsLoading && (
        <div className="patient-dashboard__loader">
          <Loader nested/>
        </div>
      )}

      {!isAssignmentsLoading && assignments.length === 0 && (
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.interventions.noAssignments',
            defaultMessage: 'No interventions assigned yet.'
          })}
          image={NotFoundImg}
        />
      )}

      {!isAssignmentsLoading && assignments.length > 0 && (
        <>
          <TodaysActivity
            activeAssignments={activeAssignments}
            programModules={programModules}
            itemsByProgram={itemsByProgram}
          />
          <MyInterventions assignments={activeAssignments} pendingCounts={pendingCounts}/>
        </>
      )}

      <QuickLinkTiles medicationsCount={medications.length}/>
    </div>
  );
};
